import { GOOGLE_SIGN_IN, STORAGE_NAMESPACE, TUNABLES } from "../config";
import { phrases } from "../i18n";
import type { EventsMode } from "../net/events";
import { jsonRecord, text } from "../net/protocol";
import { parseSignInAnswer, type SignInRefusal } from "../net/signIn";
import { make, makeButton } from "./dom";

/**
 * Google sign-in by redirect, with no Google script on the page (B3.10.1; the owner's ruling,
 * D-077): the chip sends the tab to Google's sign-in page with `response_type=id_token` and
 * `scope=openid` (the account's ID only: no email, no name), a random state and nonce kept in the
 * tab's `sessionStorage`. Google sends the tab back to the page's "/" with the ID token in the
 * address's fragment; the page checks the state, wipes the fragment and asks the Worker, which
 * checks the token itself and answers with the account's stable ID (`server/googleToken.ts`).
 * That ID then takes the guest's place in `Identity`. Only on our own URL, at an origin the
 * client lists as a redirect, never in a portal's frame.
 */

/** The request to Google the tab keeps while it is away: this tab's only. */
export const PENDING_KEY = `${STORAGE_NAMESPACE}:signin`;

/** The longest state or nonce a kept request may hold: base64url of `randomBytes`, with room. */
const MAX_WORD_CHARS = 128;

export interface PendingSignIn {
  readonly state: string;
  readonly nonce: string;
  /** The page's query before it left: the redirect comes back to "/" alone. */
  readonly search: string;
}

/** What came back from Google, read against the request the tab kept. */
export type SignInReturn =
  | { readonly kind: "token"; readonly token: string; readonly nonce: string }
  | { readonly kind: "cancelled" }
  | { readonly kind: "refused"; readonly reason: "state" | "google" };

/** How a return from Google ended, as the page records it (`signin_done`). */
export type SignInOutcome =
  | {
      readonly result: "ok";
      readonly account: string;
      /** NM2: the session and the name the account holds, where the Worker keeps names. */
      readonly session?: string;
      readonly name?: string;
    }
  | { readonly result: "refused"; readonly reason: SignInRefusal | "state" | "google" }
  | { readonly result: "cancelled" }
  | { readonly result: "unreachable" };

/** Where Google sends the tab back: the origin's "/", as the client lists it, exactly. */
export function redirectUriOf(origin: string): string {
  return `${origin}/`;
}

/** Google's sign-in page for this request: the ID token alone, for the account's ID alone. */
export function authUrlOf(request: {
  readonly redirectUri: string;
  readonly state: string;
  readonly nonce: string;
}): string {
  const url = new URL(GOOGLE_SIGN_IN.authUrl);
  url.search = new URLSearchParams({
    client_id: GOOGLE_SIGN_IN.clientId,
    redirect_uri: request.redirectUri,
    response_type: "id_token",
    scope: "openid",
    state: request.state,
    nonce: request.nonce,
  }).toString();
  return url.toString();
}

/**
 * Whether this page offers sign-in: its switch (`VITE_SIGNIN`, `?signin=on`), our own adapter,
 * not inside another site's frame (itch.io, CrazyGames), a Worker to ask, and an origin the
 * client lists as a redirect (a preview deploy is not one).
 */
export function signInHere(page: {
  readonly mode: EventsMode;
  readonly framed: boolean;
  readonly origin: string;
  readonly platform: string;
  readonly server: boolean;
}): boolean {
  const listed: readonly string[] = GOOGLE_SIGN_IN.redirectOrigins;
  return (
    page.mode !== "off" &&
    !page.framed &&
    page.platform === "local" &&
    page.server &&
    listed.includes(page.origin)
  );
}

/** A kept request, read back; undefined for none or for what is not one. */
export function pendingOf(raw: string | null): PendingSignIn | undefined {
  const value = raw === null ? undefined : jsonRecord(raw);
  const state = text(value?.state, MAX_WORD_CHARS);
  const nonce = text(value?.nonce, MAX_WORD_CHARS);
  const search = value?.search;
  return state && nonce && typeof search === "string" ? { state, nonce, search } : undefined;
}

/**
 * Google's answer in the address's fragment; undefined when the fragment is not one. A state
 * that is not the kept request's (another tab's, a link someone sent) is refused before the
 * token goes anywhere; `access_denied` is the visitor's own No.
 */
export function signInReturnOf(
  hash: string,
  pending: PendingSignIn | undefined,
): SignInReturn | undefined {
  const fields = new URLSearchParams(hash.replace(/^#/, ""));
  const token = fields.get("id_token");
  const error = fields.get("error");
  if (token === null && error === null) {
    return undefined;
  }
  if (!pending || fields.get("state") !== pending.state) {
    return { kind: "refused", reason: "state" };
  }
  if (token === null) {
    return error === "access_denied"
      ? { kind: "cancelled" }
      : { kind: "refused", reason: "google" };
  }
  return { kind: "token", token, nonce: pending.nonce };
}

/** Base64url of `bytes`. */
export function wordOf(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** A fresh state or nonce from crypto: the visitor's, not the run's. */
function freshWord(): string {
  const bytes = new Uint8Array(TUNABLES.signIn.randomBytes);
  crypto.getRandomValues(bytes);
  return wordOf(bytes);
}

/**
 * What a return from Google comes to: a token goes to the Worker (`post`, which resolves to the
 * answer's text whatever its status), anything else ends here. An answer that is not the
 * Worker's (an older Worker, no network) is "unreachable".
 */
export async function outcomeOf(
  returned: SignInReturn,
  post: (body: string) => Promise<string>,
): Promise<SignInOutcome> {
  if (returned.kind === "cancelled") {
    return { result: "cancelled" };
  }
  if (returned.kind === "refused") {
    return { result: "refused", reason: returned.reason };
  }
  let raw: string;
  try {
    raw = await post(JSON.stringify({ token: returned.token, nonce: returned.nonce }));
  } catch {
    return { result: "unreachable" };
  }
  const answer = parseSignInAnswer(raw);
  if (!answer) {
    return { result: "unreachable" };
  }
  return "account" in answer
    ? { result: "ok", ...answer }
    : { result: "refused", reason: answer.refused };
}

/** Leaves for Google's sign-in page; the tab keeps the state, the nonce and the page's query. */
export function beginSignIn(): void {
  const pending: PendingSignIn = {
    state: freshWord(),
    nonce: freshWord(),
    search: location.search,
  };
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // Not kept: the return is refused ("state"), and the visitor stays a guest.
  }
  const redirectUri = redirectUriOf(location.origin);
  location.assign(authUrlOf({ redirectUri, state: pending.state, nonce: pending.nonce }));
}

/**
 * Google's answer, taken off the address before anything reads it: the fragment goes and the
 * page's query comes back (`history.replaceState`), so the token is in no later address, no
 * reload and no link. Undefined when the page did not come back from Google.
 */
export function takeSignInReturn(): SignInReturn | undefined {
  let pending: PendingSignIn | undefined;
  try {
    pending = pendingOf(sessionStorage.getItem(PENDING_KEY));
  } catch {
    pending = undefined;
  }
  const returned = signInReturnOf(location.hash, pending);
  if (returned === undefined) {
    return undefined;
  }
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Nothing kept to remove.
  }
  history.replaceState(null, "", `${location.pathname}${pending?.search ?? location.search}`);
  return returned;
}

/** Google's "G", as its sign-in buttons show it. */
const G_LOGO =
  '<svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">' +
  '<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>' +
  '<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>' +
  '<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>' +
  '<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>' +
  "</svg>";

/** What the chip says after a return that did not sign in; a visitor's own No says nothing. */
export function noteOf(outcome: SignInOutcome | undefined): string | undefined {
  return outcome?.result === "refused" || outcome?.result === "unreachable"
    ? phrases().front.signIn.failed
    : undefined;
}

/**
 * The chip under the name on the front page: "Sign in with Google", or "Signed in with Google"
 * with Sign out, which shows the account's code on hover: the one thing a player can send us to
 * have its records found. Signing in is optional: Play now and the duels never wait for it.
 */
export function mountSignInChip(options: {
  /** The signed-in account's ID, or undefined for a guest. */
  readonly account: string | undefined;
  readonly note: string | undefined;
  readonly signIn: () => void;
  readonly signOut: () => void;
}): void {
  // NM2: under the name's line, where the page has one.
  const anchor = document.getElementById("name-note") ?? document.getElementById("player-name");
  if (!anchor) {
    return;
  }
  document.getElementById("signin")?.remove();
  const words = phrases().front.signIn;
  const chip = make("div", { id: "signin" });
  if (options.account) {
    const out = makeButton({ id: "signout-button", text: words.signOut });
    out.addEventListener("click", options.signOut);
    const state = make("span", { className: "signin-state", text: words.signedIn });
    state.title = words.code(options.account);
    chip.append(state, out);
  } else {
    const button = makeButton({ id: "signin-button" });
    const logo = make("span", { className: "signin-logo" });
    logo.innerHTML = G_LOGO;
    button.append(logo, make("span", { text: words.signIn }));
    button.addEventListener("click", options.signIn);
    chip.append(button);
    if (options.note) {
      const note = make("span", { className: "signin-note", text: options.note });
      note.setAttribute("role", "status");
      chip.append(note);
    }
  }
  anchor.insertAdjacentElement("afterend", chip);
}
